import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';

const typeOptions = [
  ['DATABASE', 'Banco de dados'],
  ['API', 'API REST'],
  ['SQL', 'SQL'],
  ['CSV', 'CSV'],
  ['EXCEL', 'Excel'],
  ['JSON', 'JSON'],
  ['MANUAL', 'Manual'],
];
const triggerOptions = [
  ['ON_OPEN', 'Ao abrir'],
  ['ON_PREVIEW', 'Ao visualizar'],
  ['ON_PRINT', 'Ao imprimir'],
  ['MANUAL', 'Manual'],
];

const blank = () => ({
  name: '',
  type: 'CSV',
  description: '',
  trigger: 'ON_PRINT',
  configText: '{\n  "location": ""\n}',
});

function parseJsonField(label, value) {
  try {
    return JSON.parse(value || '{}');
  } catch {
    throw new Error(`A configuração "${label}" precisa ser um JSON válido.`);
  }
}

export default function DataSources({ user }) {
  const canManage = user?.role === 'ADMIN' || user?.role === 'DESIGNER';
  const [list, setList] = useState([]);
  const [form, setForm] = useState(blank());
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');

  async function load() {
    try {
      setList(await api.listDataSources());
    } catch (e) {
      setError(e.message);
    }
  }

  useEffect(() => {
    load();
  }, []);

  function update(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  async function create(e) {
    e.preventDefault();
    setError('');
    setStatus('');
    try {
      const payload = { ...form, config: parseJsonField('da fonte', form.configText) };
      delete payload.configText;
      await api.createDataSource(payload);
      setForm(blank());
      setStatus('Conector cadastrado.');
      await load();
    } catch (err) {
      setError(err.message);
    }
  }

  async function toggleStatus(source) {
    try {
      await api.updateDataSource(source.id, { status: source.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE' });
      await load();
    } catch (err) {
      setError(err.message);
    }
  }

  async function remove(source) {
    if (!confirm(`Remover o conector "${source.name}"?`)) return;
    try {
      await api.deleteDataSource(source.id);
      setStatus('Conector removido.');
      await load();
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <div className="flex flex-col gap-base">
      <div>
        <h1 className="studio-uniform-title font-h2 text-h2 text-on-surface">Fontes de Dados</h1>
        <p className="font-body-md text-body-md text-on-surface-variant mt-2">
          Conectores externos que alimentam etiquetas com dados dinâmicos.
        </p>
      </div>

      {error && <p className="text-[11px] text-error">{error}</p>}
      {status && <p className="rounded-2xl border border-primary/10 bg-primary-container/40 px-4 py-3 text-[10px] text-on-primary-container">{status}</p>}

      {canManage && (
        <form onSubmit={create} className="bg-surface rounded-2xl shadow-sm p-6 flex flex-col gap-4">
          <h3 className="studio-uniform-title font-h3 text-h3 text-on-surface">Novo conector</h3>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <label className="flex flex-col gap-2">
              <span className="text-[10px] font-semibold text-on-surface-variant">Nome</span>
              <input value={form.name} onChange={(e) => update('name', e.target.value)} className="rounded-2xl border border-surface-container-high bg-surface px-4 py-3 text-[11px] text-on-surface outline-none focus:border-primary focus:ring-2 focus:ring-primary/15" />
            </label>
            <label className="flex flex-col gap-2">
              <span className="text-[10px] font-semibold text-on-surface-variant">Tipo</span>
              <select value={form.type} onChange={(e) => update('type', e.target.value)} className="rounded-2xl border border-surface-container-high bg-surface px-4 py-3 text-[11px] text-on-surface outline-none focus:border-primary focus:ring-2 focus:ring-primary/15">
                {typeOptions.map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-2">
              <span className="text-[10px] font-semibold text-on-surface-variant">Quando carregar</span>
              <select value={form.trigger} onChange={(e) => update('trigger', e.target.value)} className="rounded-2xl border border-surface-container-high bg-surface px-4 py-3 text-[11px] text-on-surface outline-none focus:border-primary focus:ring-2 focus:ring-primary/15">
                {triggerOptions.map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-2">
              <span className="text-[10px] font-semibold text-on-surface-variant">Descrição</span>
              <input value={form.description} onChange={(e) => update('description', e.target.value)} className="rounded-2xl border border-surface-container-high bg-surface px-4 py-3 text-[11px] text-on-surface outline-none focus:border-primary focus:ring-2 focus:ring-primary/15" />
            </label>
            <label className="flex flex-col gap-2 md:col-span-2 xl:col-span-4">
              <span className="text-[10px] font-semibold text-on-surface-variant">Configuração (JSON)</span>
              <textarea value={form.configText} onChange={(e) => update('configText', e.target.value)} rows={4} className="rounded-2xl border border-surface-container-high bg-surface px-4 py-3 font-mono text-[10px] text-on-surface outline-none focus:border-primary focus:ring-2 focus:ring-primary/15" />
            </label>
          </div>
          <div>
            <button type="submit" className="bg-primary hover:bg-surface-tint text-on-primary font-label-sm text-label-sm px-6 py-3 rounded-lg transition-all shadow-md shadow-primary/20">
              Cadastrar conector
            </button>
          </div>
        </form>
      )}

      <div className="grid gap-6 md:grid-cols-2">
        {list.length === 0 ? (
          <p className="col-span-full text-[11px] text-on-surface-variant">
            Nenhum conector configurado no tenant.
          </p>
        ) : (
          list.map((source) => (
            <div key={source.id} className="bg-surface rounded-2xl shadow-sm p-6 flex flex-col gap-3 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div className="w-12 h-12 rounded-xl bg-primary-container text-on-primary-container flex items-center justify-center">
                  <span className="material-symbols-outlined">database</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-surface-variant text-on-surface-variant font-label-sm text-label-sm">
                    {source.type}
                  </span>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full font-label-sm text-label-sm ${source.status === 'ACTIVE' ? 'bg-primary-container text-on-primary-container' : 'bg-surface-variant text-on-surface-variant'}`}>
                    {source.status === 'ACTIVE' ? 'Ativo' : 'Inativo'}
                  </span>
                </div>
              </div>
              <h3 className="studio-uniform-title font-h3 text-h3 text-on-surface">{source.name}</h3>
              {source.description && <p className="font-body-md text-body-md text-on-surface-variant">{source.description}</p>}
              <div className="mt-auto flex items-center justify-between">
                <span className="text-[11px] text-on-surface-variant">Carrega: {triggerOptions.find(([v]) => v === source.trigger)?.[1] || source.trigger}</span>
                <span className="text-[11px] text-on-surface-variant">{source._count?.printJobs ?? 0} trabalho(s)</span>
              </div>
              {canManage && (
                <div className="flex gap-2">
                  <button onClick={() => toggleStatus(source)} className="rounded-xl border border-surface-container-high px-3 py-2 text-[10px] font-medium text-on-surface">
                    {source.status === 'ACTIVE' ? 'Desativar' : 'Ativar'}
                  </button>
                  <button onClick={() => remove(source)} className="rounded-xl border border-error/20 bg-error-container/50 px-3 py-2 text-[10px] font-medium text-error">
                    Excluir
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
